import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Book, ChevronRight, Crown, Loader2, Calculator, TrendingDown, Gavel, Wrench, ArrowLeft, Eye, Link2, Copy, Check, ClipboardList, Target, Users, ExternalLink, Star, Sparkles, BadgeDollarSign } from 'lucide-react';
import { getAuthToken } from '../../utils/auth';
import { CostCalculatorEmbed, DealAnalyzerEmbed, NegotiationCoachEmbed } from './VIPToolsEmbed';
import SalesWarBoard from '../dashboard/SalesWarBoard';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Full sellable catalog — used by the super-admin section to expose every
// tool, its public squeeze/sales page (/checkout/:id) and its buyer list.
const ADMIN_PRODUCTS = [
  { id: 'cost_calculator', label: 'Cost Calculator', icon: Calculator, color: '#22c55e', desc: 'True ownership cost with credit score + DTI affordability.' },
  { id: 'rv_comparison', label: 'RV vs RV Comparison', icon: Star, color: '#0ea5e9', desc: 'Side-by-side AI comparison of any two RVs.' },
  { id: 'deal_analyzer', label: 'Deal Analyzer', icon: TrendingDown, color: '#6366f1', desc: 'Is the quoted price actually fair? AI verdict.' },
  { id: 'negotiation_coach', label: 'AI Negotiation Coach', icon: Gavel, color: '#a855f7', desc: 'Scripts and strategy to negotiate the deal.' },
  { id: 'daily_matches', label: 'RV Daily Matches', icon: Sparkles, color: '#f59e0b', desc: 'Fresh personalized RV matches delivered daily.' },
  { id: 'buyer_toolkit', label: 'Complete Buyer Toolkit', icon: Wrench, color: '#14b8a6', desc: 'All 4 tools + Daily Matches — $59.99 bundle.' },
  { id: 'vip_buying_plan', label: 'VIP Buying Plan', icon: BadgeDollarSign, color: '#f97316', desc: '$149 tier — consultation + match review + all tools.' },
  { id: 'vip_concierge', label: 'VIP Buyer Concierge', icon: Crown, color: '#eab308', desc: '$399 tier — 30-day hands-on buying support.' },
];

const ResourcesTab = ({ isDark, dealer }) => {
  const [activeView, setActiveView] = useState('tools');
  const [activeTool, setActiveTool] = useState(null);
  const [ebooks, setEbooks] = useState(null);
  const [vipMembers, setVipMembers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [copiedLink, setCopiedLink] = useState(null);
  const [buyersProduct, setBuyersProduct] = useState(null);

  // Super admin detection — dealer prop is authoritative, localStorage fallback
  let adminEmail = (dealer?.email || '').toLowerCase();
  let isSuperAdmin = adminEmail === 'malleg85@gmail.com';
  if (!isSuperAdmin) {
    try {
      const u = JSON.parse(localStorage.getItem('user') || 'null');
      adminEmail = (u?.email || '').toLowerCase() || adminEmail;
      isSuperAdmin = adminEmail === 'malleg85@gmail.com';
    } catch { /* ignore */ }
  }

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = getAuthToken();
      try {
        const ebooksRes = await axios.get(`${API}/dealer/ebooks`, { headers: { Authorization: `Bearer ${token}` } });
        setEbooks(ebooksRes.data);
      } catch (ebookErr) {
        console.error('Error fetching ebooks:', ebookErr);
      }
      try {
        const vipRes = await axios.get(`${API}/dealer/vip-members`, { headers: { Authorization: `Bearer ${token}` } });
        setVipMembers(vipRes.data || []);
      } catch (vipErr) {
        console.error('Error fetching VIP members:', vipErr);
        setVipMembers([]);
      }
    } catch (err) {
      console.error('Error in fetchData:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const buyingGuide = ebooks?.buying_guide;
  const travelGuide = ebooks?.travel_guide;

  const getSqueezePageUrl = (ebookType) => {
    const baseUrl = window.location.origin;
    const dealerSlug = dealer?.slug;
    return dealerSlug
      ? `${baseUrl}/free-ebook/${ebookType}?dealer=${dealerSlug}`
      : `${baseUrl}/free-ebook/${ebookType}`;
  };

  const copySqueezePageLink = (ebookType) => {
    const url = getSqueezePageUrl(ebookType);
    navigator.clipboard.writeText(url);
    setCopiedLink(ebookType);
    toast.success('Squeeze page link copied!');
    setTimeout(() => setCopiedLink(null), 3000);
  };

  // Open the new premium ebook page in a new tab. The backend allows token=dealer
  // for super-admin / dealer access (vip_routes.py line 1211).
  const openEbookPage = (ebookType) => {
    window.open(`/ebook/${ebookType}?token=dealer`, '_blank', 'noopener,noreferrer');
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="animate-spin text-blue-500" size={40} />
      </div>
    );
  }

  // Buyers view (super admin): who owns this product
  if (buyersProduct) {
    return (
      <ProductBuyersView
        product={buyersProduct}
        isDark={isDark}
        onBack={() => setBuyersProduct(null)}
      />
    );
  }

  // Embedded Tool View
  if (activeTool) {
    return (
      <div data-testid="resources-tab-tool-view">
        <button
          onClick={() => setActiveTool(null)}
          className={`flex items-center gap-2 mb-4 px-3 py-2 rounded-lg transition-colors ${
            isDark ? 'text-gray-400 hover:text-white hover:bg-gray-800' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
          }`}
          data-testid="back-to-tools"
        >
          <ArrowLeft size={18} />
          <span className="text-sm font-medium">Back to Tools</span>
        </button>
        {activeTool === 'calculator' && <CostCalculatorEmbed isDark={isDark} />}
        {activeTool === 'analyzer' && <DealAnalyzerEmbed isDark={isDark} />}
        {activeTool === 'negotiation' && <NegotiationCoachEmbed isDark={isDark} />}
        {activeTool === 'warboard' && <SalesWarBoard />}
      </div>
    );
  }

  return (
    <div data-testid="resources-tab">
      {/* Sub Navigation - Mobile Friendly */}
      <div className={`flex gap-1 p-1 rounded-lg mb-6 overflow-x-auto ${isDark ? 'bg-gray-800' : 'bg-gray-100'}`}>
        {[
          { id: 'tools', label: 'VIP Tools', icon: Wrench },
          { id: 'ebooks', label: 'Ebooks', icon: Book },
          { id: 'vip', label: 'VIP Members', icon: Crown, count: vipMembers?.length || 0 },
        ].map(({ id, label, icon: Icon, count }) => (
          <button
            key={id}
            onClick={() => setActiveView(id)}
            className={`flex-1 min-w-[100px] flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
              activeView === id
                ? isDark ? 'bg-blue-600 text-white' : 'bg-white text-blue-600 shadow-sm'
                : isDark ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'
            }`}
            data-testid={`tab-${id}`}
          >
            <Icon size={18} />
            <span>{label}</span>
            {count > 0 && (
              <span className={`px-1.5 py-0.5 rounded-full text-xs ${
                activeView === id ? 'bg-white/20 text-white' : isDark ? 'bg-gray-700 text-gray-400' : 'bg-gray-200 text-gray-600'
              }`}>
                {count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* VIP Tools Section */}
      {activeView === 'tools' && (
        <div className="space-y-6">
          <div className={`rounded-xl p-4 md:p-6 ${isDark ? 'bg-gray-800' : 'bg-white'} shadow-sm`}>
            <div className="flex items-center gap-3 mb-2">
              <Crown className="text-amber-500" size={24} />
              <h2 className={`text-lg md:text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                VIP Tools Suite
              </h2>
            </div>
            <p className={`text-sm mb-6 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              Full access to all premium tools.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
              {/* Cost Calculator */}
              <button onClick={() => setActiveTool('calculator')} className="block text-left w-full" data-testid="open-cost-calculator">
                <div className={`p-4 md:p-6 rounded-xl border-2 transition-all hover:scale-[1.02] hover:shadow-lg ${
                  isDark ? 'bg-gray-700/50 border-gray-600 hover:border-green-500' : 'bg-gray-50 border-gray-200 hover:border-green-500'
                }`}>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2 md:p-3 rounded-lg bg-green-500/20">
                      <Calculator className="text-green-500" size={20} />
                    </div>
                    <h3 className={`font-bold text-sm md:text-base ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      Cost Calculator
                    </h3>
                  </div>
                  <p className={`text-xs md:text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    Calculate 5-year ownership costs including depreciation, maintenance, storage.
                  </p>
                  <div className="mt-3 md:mt-4 flex items-center text-green-500 text-sm font-medium">
                    Open <ChevronRight size={16} className="ml-1" />
                  </div>
                </div>
              </button>

              {/* Deal Analyzer */}
              <button onClick={() => setActiveTool('analyzer')} className="block text-left w-full" data-testid="open-deal-analyzer">
                <div className={`p-4 md:p-6 rounded-xl border-2 transition-all hover:scale-[1.02] hover:shadow-lg ${
                  isDark ? 'bg-gray-700/50 border-gray-600 hover:border-blue-500' : 'bg-gray-50 border-gray-200 hover:border-blue-500'
                }`}>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2 md:p-3 rounded-lg bg-blue-500/20">
                      <TrendingDown className="text-blue-500" size={20} />
                    </div>
                    <h3 className={`font-bold text-sm md:text-base ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      Deal Analyzer
                    </h3>
                  </div>
                  <p className={`text-xs md:text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    Analyze any RV deal. Upload quote screenshots for AI analysis.
                  </p>
                  <div className="mt-3 md:mt-4 flex items-center text-blue-500 text-sm font-medium">
                    Open <ChevronRight size={16} className="ml-1" />
                  </div>
                </div>
              </button>

              {/* Negotiation Coach */}
              <button onClick={() => setActiveTool('negotiation')} className="block text-left w-full" data-testid="open-negotiation-coach">
                <div className={`p-4 md:p-6 rounded-xl border-2 transition-all hover:scale-[1.02] hover:shadow-lg ${
                  isDark ? 'bg-gray-700/50 border-gray-600 hover:border-purple-500' : 'bg-gray-50 border-gray-200 hover:border-purple-500'
                }`}>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2 md:p-3 rounded-lg bg-purple-500/20">
                      <Gavel className="text-purple-500" size={20} />
                    </div>
                    <h3 className={`font-bold text-sm md:text-base ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      AI Negotiation Coach
                    </h3>
                  </div>
                  <p className={`text-xs md:text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    AI-powered negotiation strategies and scripts.
                  </p>
                  <div className="mt-3 md:mt-4 flex items-center text-purple-500 text-sm font-medium">
                    Open <ChevronRight size={16} className="ml-1" />
                  </div>
                </div>
              </button>

              {/* Customer Intake Form */}
              <Link to="/dealer/intake-form" className="block text-left w-full" data-testid="open-intake-form">
                <div className={`p-4 md:p-6 rounded-xl border-2 transition-all hover:scale-[1.02] hover:shadow-lg ${
                  isDark ? 'bg-gray-700/50 border-gray-600 hover:border-orange-500' : 'bg-gray-50 border-gray-200 hover:border-orange-500'
                }`}>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2 md:p-3 rounded-lg bg-orange-500/20">
                      <ClipboardList className="text-orange-500" size={20} />
                    </div>
                    <h3 className={`font-bold text-sm md:text-base ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      Customer Intake Form
                    </h3>
                  </div>
                  <p className={`text-xs md:text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    Digital walk-around form. Email to yourself for CRM entry.
                  </p>
                  <div className="mt-3 md:mt-4 flex items-center text-orange-500 text-sm font-medium">
                    Open <ChevronRight size={16} className="ml-1" />
                  </div>
                </div>
              </Link>

              {/* Sales War Board */}
              <button onClick={() => setActiveTool('warboard')} className="block text-left w-full" data-testid="open-sales-warboard">
                <div className={`p-4 md:p-6 rounded-xl border-2 transition-all hover:scale-[1.02] hover:shadow-lg ${
                  isDark ? 'bg-gray-700/50 border-gray-600 hover:border-red-500' : 'bg-gray-50 border-gray-200 hover:border-red-500'
                }`}>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2 md:p-3 rounded-lg bg-red-500/20">
                      <Target className="text-red-500" size={20} />
                    </div>
                    <h3 className={`font-bold text-sm md:text-base ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      Sales War Board
                    </h3>
                  </div>
                  <p className={`text-xs md:text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    Live accountability scoreboard for your monthly sales goals.
                  </p>
                  <div className="mt-3 md:mt-4 flex items-center text-red-500 text-sm font-medium">
                    Open <ChevronRight size={16} className="ml-1" />
                  </div>
                </div>
              </button>
            </div>
          </div>

          {/* Super Admin: full product catalog with squeeze pages + buyers */}
          {isSuperAdmin && (
            <div className={`rounded-xl p-4 md:p-6 ${isDark ? 'bg-gray-800' : 'bg-white'} shadow-sm`} data-testid="admin-products-section">
              <div className="flex items-center gap-3 mb-2">
                <Users className="text-blue-500" size={22} />
                <h2 className={`text-lg md:text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  All Products — Squeeze Pages &amp; Buyers
                </h2>
              </div>
              <p className={`text-sm mb-6 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                Every sellable tool and VIP tier. Open the tool, copy its public sales page, or see who owns it.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {ADMIN_PRODUCTS.map((p) => {
                  const Icon = p.icon;
                  return (
                    <div
                      key={p.id}
                      className={`p-4 rounded-xl border-2 ${isDark ? 'bg-gray-700/50 border-gray-600' : 'bg-gray-50 border-gray-200'}`}
                      data-testid={`admin-product-${p.id}`}
                    >
                      <div className="flex items-center gap-3 mb-2">
                        <div className="p-2 rounded-lg" style={{ background: `${p.color}20` }}>
                          <Icon size={18} style={{ color: p.color }} />
                        </div>
                        <h3 className={`font-bold text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>{p.label}</h3>
                      </div>
                      <p className={`text-xs mb-3 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{p.desc}</p>
                      <div className="flex flex-wrap gap-2">
                        <button
                          onClick={() => window.open(`/my-tool/${p.id}?email=${encodeURIComponent(adminEmail)}`, '_blank', 'noopener,noreferrer')}
                          className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold ${isDark ? 'bg-gray-600 text-white hover:bg-gray-500' : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-100'}`}
                          data-testid={`admin-open-${p.id}`}
                        >
                          <ExternalLink size={12} /> Open
                        </button>
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(`${window.location.origin}/checkout/${p.id}`);
                            setCopiedLink(p.id);
                            setTimeout(() => setCopiedLink(null), 2000);
                          }}
                          className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold ${copiedLink === p.id ? 'bg-emerald-600 text-white' : isDark ? 'bg-gray-600 text-white hover:bg-gray-500' : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-100'}`}
                          data-testid={`admin-squeeze-${p.id}`}
                        >
                          {copiedLink === p.id ? <Check size={12} /> : <Link2 size={12} />} {copiedLink === p.id ? 'Copied!' : 'Squeeze Page'}
                        </button>
                        <button
                          onClick={() => setBuyersProduct(p)}
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold bg-blue-600 text-white hover:bg-blue-700"
                          data-testid={`admin-buyers-${p.id}`}
                        >
                          <Users size={12} /> Buyers
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
      {activeView === 'ebooks' && (
        <div className="space-y-6">
          <div className={`rounded-xl p-4 md:p-6 ${isDark ? 'bg-gray-800' : 'bg-white'} shadow-sm`}>
            <h2 className={`text-lg md:text-xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Exclusive Ebooks
            </h2>
            <p className={`text-sm mb-6 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              Share these resources with leads to build trust.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
              {/* RV Buying Guide */}
              <div className={`p-4 md:p-6 rounded-xl border ${isDark ? 'bg-gray-700/30 border-gray-700' : 'bg-blue-50 border-blue-100'}`}>
                <div className="flex flex-col sm:flex-row items-start gap-4">
                  <div className="w-14 h-18 md:w-16 md:h-20 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-lg shrink-0">
                    <Book className="text-white" size={24} />
                  </div>
                  <div className="flex-1">
                    <h3 className={`font-bold text-base md:text-lg ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      Complete RV Buying Guide
                    </h3>
                    <p className={`text-xs md:text-sm mt-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                      Everything about purchasing their first RV.
                    </p>
                    <div className="mt-3 flex flex-wrap items-center gap-2 md:gap-3">
                      <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                        {buyingGuide?.download_count || 0} downloads
                      </span>
                      <button
                        onClick={() => openEbookPage('rv-buying-guide')}
                        className="flex items-center gap-1 text-sm text-blue-500 hover:text-blue-600 font-medium"
                        data-testid="view-buying-guide-btn"
                      >
                        <Eye size={14} /> View / Download
                      </button>
                      <span className={`text-xs ${isDark ? 'text-gray-600' : 'text-gray-300'}`}>•</span>
                      <button
                        onClick={() => copySqueezePageLink('rv-buying-guide')}
                        className={`flex items-center gap-1 text-sm font-medium ${
                          copiedLink === 'rv-buying-guide'
                            ? 'text-green-500'
                            : isDark ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'
                        }`}
                      >
                        {copiedLink === 'rv-buying-guide' ? <Check size={14} /> : <Link2 size={14} />}
                        {copiedLink === 'rv-buying-guide' ? 'Copied!' : 'Squeeze Page'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Travel Guide */}
              <div className={`p-4 md:p-6 rounded-xl border ${isDark ? 'bg-gray-700/30 border-gray-700' : 'bg-green-50 border-green-100'}`}>
                <div className="flex flex-col sm:flex-row items-start gap-4">
                  <div className="w-14 h-18 md:w-16 md:h-20 rounded-lg bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center shadow-lg shrink-0">
                    <Book className="text-white" size={24} />
                  </div>
                  <div className="flex-1">
                    <h3 className={`font-bold text-base md:text-lg ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      RV Travel & Camping Guide
                    </h3>
                    <p className={`text-xs md:text-sm mt-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                      Essential tips for new RV travelers.
                    </p>
                    <div className="mt-3 flex flex-wrap items-center gap-2 md:gap-3">
                      <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                        {travelGuide?.download_count || 0} downloads
                      </span>
                      <button
                        onClick={() => openEbookPage('rv-travel-guide')}
                        className="flex items-center gap-1 text-sm text-green-500 hover:text-green-600 font-medium"
                        data-testid="view-travel-guide-btn"
                      >
                        <Eye size={14} /> View / Download
                      </button>
                      <span className={`text-xs ${isDark ? 'text-gray-600' : 'text-gray-300'}`}>•</span>
                      <button
                        onClick={() => copySqueezePageLink('rv-travel-guide')}
                        className={`flex items-center gap-1 text-sm font-medium ${
                          copiedLink === 'rv-travel-guide'
                            ? 'text-green-500'
                            : isDark ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'
                        }`}
                      >
                        {copiedLink === 'rv-travel-guide' ? <Check size={14} /> : <Link2 size={14} />}
                        {copiedLink === 'rv-travel-guide' ? 'Copied!' : 'Squeeze Page'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* VIP Members Section */}
      {activeView === 'vip' && (
        <div className={`rounded-xl p-4 md:p-6 ${isDark ? 'bg-gray-800' : 'bg-white'} shadow-sm`}>
          <h2 className={`text-lg md:text-xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            VIP Members
          </h2>
          <p className={`text-sm mb-6 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            Leads who upgraded to VIP status.
          </p>

          {vipMembers.length === 0 ? (
            <div className={`text-center py-12 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
              <Crown size={48} className="mx-auto mb-3 opacity-50" />
              <p>No VIP members yet</p>
              <p className="text-sm">When leads upgrade, they'll appear here</p>
            </div>
          ) : (
            <div className="space-y-3">
              {vipMembers.map((member) => (
                <div
                  key={member.id}
                  className={`p-3 md:p-4 rounded-lg border ${isDark ? 'bg-gray-700 border-gray-600' : 'bg-amber-50 border-amber-100'}`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${isDark ? 'bg-amber-900/50' : 'bg-amber-100'}`}>
                      <Crown className="text-amber-500" size={20} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`font-medium truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {member.name}
                      </p>
                      <p className={`text-sm truncate ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        {member.email}
                      </p>
                    </div>
                    <div className="text-right shrink-0">
                      <span className="inline-block px-2 py-1 rounded-full text-xs bg-amber-100 text-amber-700">
                        VIP
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const ProductBuyersView = ({ product, isDark, onBack }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const token = getAuthToken();
        const res = await axios.get(`${API}/admin/product-buyers/${product.id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setData(res.data);
      } catch (e) {
        setError(e.response?.data?.detail || 'Failed to load buyers');
      }
    })();
  }, [product.id]);

  const Icon = product.icon;
  return (
    <div data-testid="product-buyers-view">
      <button
        onClick={onBack}
        className={`inline-flex items-center gap-2 mb-4 text-sm font-medium ${isDark ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}
        data-testid="buyers-back-btn"
      >
        <ArrowLeft size={16} /> Back to Resources
      </button>
      <div className={`rounded-xl p-4 md:p-6 ${isDark ? 'bg-gray-800' : 'bg-white'} shadow-sm`}>
        <div className="flex items-center gap-3 mb-1">
          <div className="p-2 rounded-lg" style={{ background: `${product.color}20` }}>
            <Icon size={20} style={{ color: product.color }} />
          </div>
          <h2 className={`text-lg md:text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {product.label} — Buyers
          </h2>
        </div>
        <p className={`text-sm mb-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
          Every account that owns this product (direct purchases, bundles and VIP tiers included).
        </p>
        {error && <p className="text-sm text-red-500" data-testid="buyers-error">{error}</p>}
        {!data && !error && <Loader2 className="w-5 h-5 animate-spin text-blue-500" />}
        {data && (
          <>
            <p className={`text-sm font-semibold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`} data-testid="buyers-count">
              {data.count} buyer{data.count === 1 ? '' : 's'}
            </p>
            {data.count === 0 ? (
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>No buyers yet.</p>
            ) : (
              <div className="space-y-2">
                {data.buyers.map((b) => (
                  <div
                    key={b.email}
                    className={`flex flex-wrap items-center justify-between gap-2 p-3 rounded-lg border ${isDark ? 'bg-gray-700/40 border-gray-700' : 'bg-gray-50 border-gray-200'}`}
                    data-testid={`buyer-row-${b.email}`}
                  >
                    <div>
                      <p className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{b.name || '—'}</p>
                      <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{b.email}</p>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      {(b.via || []).map((v) => (
                        <span key={v} className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${isDark ? 'bg-gray-600 text-gray-200' : 'bg-gray-200 text-gray-700'}`}>
                          via {v.replace(/_/g, ' ')}
                        </span>
                      ))}
                      {b.first_seen && (
                        <span className={`text-[11px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                          {String(b.first_seen).slice(0, 10)}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ResourcesTab;
